import { useMemo, useState } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import ImageFrame from "@/components/site/ImageFrame";
import Reveal from "@/components/site/Reveal";
import Seo from "@/components/site/Seo";
import PageHeader from "@/components/site/PageHeader";

const fetcher = (url) => api.get(url).then((r) => r.data);
const RATIOS = ["3 / 4", "1 / 1", "4 / 5", "4 / 3"];

export default function Galleria() {
  const { data, isLoading } = useSWR("/gallery", fetcher, { revalidateOnFocus: false });
  const { settings } = useSettings();
  const [filter, setFilter] = useState("tutte");

  const categories = useMemo(() => {
    const set = new Set((data || []).map((i) => i.category).filter(Boolean));
    return ["tutte", ...Array.from(set)];
  }, [data]);

  const images = (data || []).filter((i) => filter === "tutte" || i.category === filter);

  return (
    <>
      <Seo
        title="Galleria · Osteria Aterrana, borgo di Aterrana a Montoro"
        description="Immagini dell'Osteria Aterrana e del borgo storico di Aterrana, a Montoro (AV)."
        path="/galleria"
      />
      <PageHeader
        eyebrow="Galleria"
        title="L'osteria e il borgo"
        lead="Le fotografie sono le nostre: le stiamo raccogliendo poco a poco, come tutto il resto."
        label="INTERIOR PHOTO"
        alt="La porta in legno dell'osteria aperta sul muro in pietra del borgo"
        src={settings.gallery_header_image || data?.[0]?.image}
      />

      <section className="section-y bg-cream">
        <div className="container-ed">
          {categories.length > 2 && (
            <div className="flex flex-wrap gap-x-7 gap-y-3 pb-12 border-b border-wood/15">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setFilter(c)}
                  data-testid={`gallery-filter-${c}`}
                  className={`eyebrow transition-colors duration-300 ${
                    filter === c ? "text-sage" : "text-wood/45 hover:text-wood"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          {isLoading && <p className="mt-12 text-sm text-foreground/55">Carichiamo le immagini…</p>}

          {!isLoading && images.length === 0 && (
            <p className="mt-12 text-sm text-foreground/60" data-testid="gallery-empty">
              Non ci sono ancora immagini in questa sezione.
            </p>
          )}

          <div className="mt-12 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:balance]" data-testid="gallery-grid">
            {images.map((img, i) => (
              <Reveal key={img.id} delay={Math.min(i * 0.05, 0.4)} className="mb-5 break-inside-avoid">
                <ImageFrame
                  src={img.image}
                  label={img.placeholder_label || "BORGO PHOTO"}
                  alt={img.alt}
                  ratio={RATIOS[i % RATIOS.length]}
                  testId={`gallery-item-${i}`}
                />
                {img.alt && <p className="mt-2 text-xs tracking-wide text-foreground/45">{img.alt}</p>}
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
